import React, { useState, useEffect } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { getSettings, saveSettings, syncFromDB } from "./demoConfig";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { ArrowLeft, Save, Check, Link, Video, CreditCard, MessageSquare, Mic, Loader2, X, Zap, CalendarClock, Calendar as CalendarIcon, Bot } from "lucide-react";
import CloserCalendar from "./CloserCalendar";
import { waFlowGetStatus, waFlowSetup } from "./api";

const backendUrl = process.env.REACT_APP_BACKEND_URL || "";

export default function Settings() {
  const navigate = useNavigate();
  const { auth } = useOutletContext();
  const [settings, setSettings] = useState({});
  const [saved, setSaved] = useState(false);
  const [fathomTest, setFathomTest] = useState({ loading: false, result: null });
  const [waTest, setWaTest] = useState({ loading: false, result: null });
  const [flowStatus, setFlowStatus] = useState({ configured: false, flowId: null, endpointUrl: null });
  const [flowSetup, setFlowSetup] = useState({ loading: false, error: null, success: null });

  useEffect(() => {
    waFlowGetStatus().then(setFlowStatus).catch(() => {});
  }, []);

  const runFlowSetup = async (force = false) => {
    setFlowSetup({ loading: true, error: null, success: null });
    try {
      const result = await waFlowSetup(force);
      setFlowSetup({ loading: false, error: null, success: result });
      const fresh = await waFlowGetStatus();
      setFlowStatus(fresh);
    } catch (e) {
      setFlowSetup({ loading: false, error: e?.message || "Setup failed", success: null });
    }
  };

  useEffect(() => {
    setSettings(getSettings());
  }, []);

  const update = (key, value) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
    setSaved(false);
  };

  const handleSave = () => {
    saveSettings(settings);
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  const testFathomConnection = async () => {
    setFathomTest({ loading: true, result: null });
    try {
      const resp = await fetch(`${backendUrl}/api/fathom-test`);
      const data = await resp.json();
      setFathomTest({ loading: false, result: data });
    } catch (e) {
      setFathomTest({ loading: false, result: { success: false, message: "Connection failed — check your API key" } });
    }
  };

  const testWhatsAppConnection = async () => {
    setWaTest({ loading: true, result: null });
    try {
      const resp = await fetch(`${backendUrl}/api/demo/whatsapp/status`);
      const data = await resp.json();
      if (data.configured) {
        // Try to fetch templates to confirm token works
        const tResp = await fetch(`${backendUrl}/api/demo/whatsapp/templates`);
        if (tResp.ok) {
          const tData = await tResp.json();
          setWaTest({ loading: false, result: { success: true, message: `Connected — ${tData.count} template(s) found` } });
        } else {
          const err = await tResp.json();
          setWaTest({ loading: false, result: { success: false, message: err.detail || "Token may be expired" } });
        }
      } else {
        setWaTest({ loading: false, result: { success: false, message: "Not configured — add credentials below" } });
      }
    } catch (e) {
      setWaTest({ loading: false, result: { success: false, message: "Connection failed" } });
    }
  };

  const saveWhatsAppSettings = async () => {
    try {
      await fetch(`${backendUrl}/api/demo/whatsapp/settings`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          accessToken: settings.whatsappAccessToken || "",
          phoneNumberId: settings.whatsappPhoneNumberId || "",
          businessAccountId: settings.whatsappBusinessAccountId || "",
          verifyToken: settings.whatsappVerifyToken || "",
        }),
      });
    } catch (e) {
      console.error("Failed to save WhatsApp settings:", e);
    }
  };

  const handleSaveAll = () => {
    saveSettings(settings);
    saveWhatsAppSettings();
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  return (
    <div className="settings-page" data-testid="settings-page">
      <div className="settings-topbar settings-topbar-slim">
        <h1 className="settings-title">Settings</h1>
        <Button onClick={handleSaveAll} className="settings-save-btn" data-testid="settings-save-btn">
          {saved ? <><Check className="w-4 h-4" /> Saved!</> : <><Save className="w-4 h-4" /> Save Changes</>}
        </Button>
      </div>

      <div className="settings-content">
        <div className="settings-grid">
          {/* Meeting Links */}
          <div className="settings-card" data-testid="settings-meeting">
            <div className="settings-card-header">
              <Link className="w-5 h-5" />
              <div>
                <h3>Meeting Links</h3>
                <p>Default links for demos and pre-demo messages</p>
              </div>
            </div>
            <div className="settings-card-body">
              <div className="settings-field">
                <Label>Google Meet / Zoom Link</Label>
                <Input
                  value={settings.meetLink || ""}
                  onChange={(e) => update("meetLink", e.target.value)}
                  placeholder="https://meet.google.com/xxx-xxxx-xxx"
                  data-testid="settings-meet-link"
                />
                <span className="settings-hint">Used in pre-demo show-up messages</span>
              </div>
            </div>
          </div>

          {/* Admin Notifications */}
          <div className="settings-card" data-testid="settings-admin-alerts">
            <div className="settings-card-header">
              <MessageSquare className="w-5 h-5" />
              <div>
                <h3>Admin Alerts</h3>
                <p>Get a WhatsApp ping the moment a new lead comes in. Add as many recipients as you want.</p>
              </div>
            </div>
            <div className="settings-card-body">
              {(settings.adminNotifyPhones || []).map((rcp, idx) => (
                <div key={idx} className="admin-alert-row" data-testid={`admin-alert-row-${idx}`}>
                  <Input
                    type="text"
                    placeholder="Name (e.g., Abdul)"
                    value={rcp.name || ""}
                    onChange={(e) => {
                      const list = [...(settings.adminNotifyPhones || [])];
                      list[idx] = { ...list[idx], name: e.target.value };
                      update("adminNotifyPhones", list);
                    }}
                    className="admin-alert-name-input"
                    data-testid={`admin-alert-name-${idx}`}
                  />
                  <Input
                    type="tel"
                    placeholder="919591775674"
                    value={rcp.phone || ""}
                    onChange={(e) => {
                      const list = [...(settings.adminNotifyPhones || [])];
                      list[idx] = { ...list[idx], phone: e.target.value.replace(/\D/g, "") };
                      update("adminNotifyPhones", list);
                    }}
                    className="admin-alert-phone-input"
                    data-testid={`admin-alert-phone-${idx}`}
                  />
                  <label className="admin-alert-enable">
                    <input
                      type="checkbox"
                      checked={rcp.enabled !== false}
                      onChange={(e) => {
                        const list = [...(settings.adminNotifyPhones || [])];
                        list[idx] = { ...list[idx], enabled: e.target.checked };
                        update("adminNotifyPhones", list);
                      }}
                      data-testid={`admin-alert-enabled-${idx}`}
                    />
                    <span>On</span>
                  </label>
                  <button
                    type="button"
                    className="admin-alert-remove"
                    onClick={() => {
                      const list = (settings.adminNotifyPhones || []).filter((_, i) => i !== idx);
                      update("adminNotifyPhones", list);
                    }}
                    data-testid={`admin-alert-remove-${idx}`}
                    title="Remove"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
              <button
                type="button"
                className="admin-alert-add"
                onClick={() => {
                  const list = [...(settings.adminNotifyPhones || []), { name: "", phone: "", enabled: true }];
                  update("adminNotifyPhones", list);
                }}
                data-testid="admin-alert-add"
              >
                + Add recipient
              </button>
              <span className="settings-hint">Country code + number, no +. Each person gets a personalized 🔔 NEW LEAD alert with name, phone, source, and first message.</span>
            </div>
          </div>

          {/* Work Hours / Closer Calendar */}
          <div className="settings-card" data-testid="settings-workhours">
            <div className="settings-card-header">
              <CalendarClock className="w-5 h-5" />
              <div>
                <h3>Closer Calendar</h3>
                <p>Only slots within these hours can be booked. Prevents overlapping meetings.</p>
              </div>
            </div>
            <div className="settings-card-body">
              <div className="settings-field">
                <Label>Timezone</Label>
                <select
                  className="settings-select"
                  value={(settings.workHours?.timezone) || "America/New_York"}
                  onChange={(e) => update("workHours", { ...(settings.workHours || {}), timezone: e.target.value })}
                  data-testid="workhours-tz"
                >
                  <option value="America/New_York">America/New_York (EST/EDT)</option>
                  <option value="Asia/Kolkata">Asia/Kolkata (IST)</option>
                  <option value="Europe/London">Europe/London (GMT/BST)</option>
                  <option value="America/Los_Angeles">America/Los_Angeles (PST/PDT)</option>
                  <option value="UTC">UTC</option>
                </select>
                <span className="settings-hint">Closers are available in this timezone</span>
              </div>

              <div className="settings-field settings-field-row">
                <div style={{ flex: 1 }}>
                  <Label>Start hour (0-23)</Label>
                  <Input
                    type="number" min="0" max="23"
                    value={(settings.workHours?.startHour ?? 8)}
                    onChange={(e) => update("workHours", { ...(settings.workHours || {}), startHour: parseInt(e.target.value) || 0 })}
                    data-testid="workhours-start"
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <Label>End hour (0-23)</Label>
                  <Input
                    type="number" min="1" max="23"
                    value={(settings.workHours?.endHour ?? 14)}
                    onChange={(e) => update("workHours", { ...(settings.workHours || {}), endHour: parseInt(e.target.value) || 14 })}
                    data-testid="workhours-end"
                  />
                </div>
              </div>

              <div className="settings-field">
                <Label>Working days</Label>
                <div className="workhours-days" data-testid="workhours-days">
                  {[
                    { i: 0, label: "Mon" }, { i: 1, label: "Tue" }, { i: 2, label: "Wed" },
                    { i: 3, label: "Thu" }, { i: 4, label: "Fri" }, { i: 5, label: "Sat" }, { i: 6, label: "Sun" },
                  ].map(d => {
                    const days = settings.workHours?.days || [0,1,2,3,4,5];
                    const active = days.includes(d.i);
                    return (
                      <button
                        key={d.i}
                        type="button"
                        className={`workhours-day-btn ${active ? 'active' : ''}`}
                        onClick={() => {
                          const next = active ? days.filter(x => x !== d.i) : [...days, d.i].sort();
                          update("workHours", { ...(settings.workHours || {}), days: next });
                        }}
                        data-testid={`workhours-day-${d.i}`}
                      >
                        {d.label}
                      </button>
                    );
                  })}
                </div>
                <span className="settings-hint">Click to toggle days</span>
              </div>

              <div className="settings-field">
                <Label>Slot duration (minutes)</Label>
                <select
                  className="settings-select"
                  value={(settings.workHours?.slotMinutes) || 30}
                  onChange={(e) => update("workHours", { ...(settings.workHours || {}), slotMinutes: parseInt(e.target.value) })}
                  data-testid="workhours-slot"
                >
                  <option value={15}>15 min</option>
                  <option value={30}>30 min</option>
                  <option value={45}>45 min</option>
                  <option value={60}>60 min</option>
                </select>
                <span className="settings-hint">Buffer between back-to-back meetings</span>
              </div>

              <WorkHoursPreview api={backendUrl} />
            </div>
          </div>

          {/* Payment Links */}
          <div className="settings-card" data-testid="settings-payment">
            <div className="settings-card-header">
              <CreditCard className="w-5 h-5" />
              <div>
                <h3>Payment</h3>
                <p>Payment links for follow-up messages and live demo</p>
              </div>
            </div>
            <div className="settings-card-body">
              <div className="settings-field">
                <Label>Razorpay / Payment Link</Label>
                <Input
                  value={settings.razorpayLink || ""}
                  onChange={(e) => update("razorpayLink", e.target.value)}
                  placeholder="https://razorpay.me/bulkjobapply"
                  data-testid="settings-razorpay-link"
                />
              </div>
              <div className="settings-field">
                <Label>PayPal Link</Label>
                <Input
                  value={settings.paypalLink || ""}
                  onChange={(e) => update("paypalLink", e.target.value)}
                  placeholder="https://paypal.me/bulkjobapply"
                  data-testid="settings-paypal-link"
                />
              </div>
              <div className="settings-field">
                <Label>UPI ID</Label>
                <Input
                  value={settings.upiId || ""}
                  onChange={(e) => update("upiId", e.target.value)}
                  placeholder="bulkjobapply@upi"
                  data-testid="settings-upi-id"
                />
              </div>
              <div className="settings-field">
                <Label>Remitly / Wire Transfer Link</Label>
                <Input
                  value={settings.remitlyLink || ""}
                  onChange={(e) => update("remitlyLink", e.target.value)}
                  placeholder="Wire transfer instructions URL"
                  data-testid="settings-remitly-link"
                />
              </div>
              <span className="settings-hint">These appear on the Close screen and in follow-up messages</span>
            </div>
          </div>

          {/* Video URLs */}
          <div className="settings-card" data-testid="settings-videos">
            <div className="settings-card-header">
              <Video className="w-5 h-5" />
              <div>
                <h3>Demo Videos</h3>
                <p>Product video URLs shown during the live demo</p>
              </div>
            </div>
            <div className="settings-card-body">
              <div className="settings-field">
                <Label>ChillApply Demo Video</Label>
                <Input
                  value={settings.chillApplyVideoUrl || ""}
                  onChange={(e) => update("chillApplyVideoUrl", e.target.value)}
                  placeholder="https://youtube.com/watch?v=..."
                  data-testid="settings-chillapply-video"
                />
              </div>
              <div className="settings-field">
                <Label>ChillRefer Demo Video</Label>
                <Input
                  value={settings.chillReferVideoUrl || ""}
                  onChange={(e) => update("chillReferVideoUrl", e.target.value)}
                  placeholder="https://youtube.com/watch?v=..."
                  data-testid="settings-chillrefer-video"
                />
              </div>
              <span className="settings-hint">These links appear on Screen 4 (How We Get You Hired)</span>
            </div>
          </div>

          {/* WhatsApp Defaults */}
          <div className="settings-card" data-testid="settings-whatsapp">
            <div className="settings-card-header">
              <MessageSquare className="w-5 h-5" />
              <div>
                <h3>Message Defaults</h3>
                <p>Default values for auto-generated messages</p>
              </div>
            </div>
            <div className="settings-card-body">
              <div className="settings-field">
                <Label>Default Demo Time</Label>
                <Input
                  value={settings.defaultDemoTime || ""}
                  onChange={(e) => update("defaultDemoTime", e.target.value)}
                  placeholder="e.g. 3:00 PM IST"
                  data-testid="settings-demo-time"
                />
                <span className="settings-hint">Used in show-up messages when no time is set on the lead</span>
              </div>
              <div className="settings-field">
                <Label>WhatsApp Closer Signature</Label>
                <Input
                  value={settings.closerSignature || ""}
                  onChange={(e) => update("closerSignature", e.target.value)}
                  placeholder={`e.g. ${auth?.name || "Your Name"}, BulkJobApply`}
                  data-testid="settings-signature"
                />
                <span className="settings-hint">Override the default signature in follow-up messages</span>
              </div>
            </div>
          </div>

          {/* WhatsApp Cloud API */}
          <div className="settings-card" data-testid="settings-whatsapp-api">
            <div className="settings-card-header">
              <Zap className="w-5 h-5" />
              <div>
                <h3>WhatsApp Cloud API</h3>
                <p>Connect Meta WhatsApp Cloud API for automated messages</p>
              </div>
            </div>
            <div className="settings-card-body">
              <div className="settings-field">
                <Label>Access Token</Label>
                <Input
                  type="password"
                  value={settings.whatsappAccessToken || ""}
                  onChange={(e) => update("whatsappAccessToken", e.target.value)}
                  placeholder="EAARZCUMKYr..."
                  data-testid="settings-wa-token"
                />
                <span className="settings-hint">Generate at Meta Developer Portal → WhatsApp → API Setup</span>
              </div>
              <div className="settings-field">
                <Label>Phone Number ID</Label>
                <Input
                  value={settings.whatsappPhoneNumberId || ""}
                  onChange={(e) => update("whatsappPhoneNumberId", e.target.value)}
                  placeholder="105070919479..."
                  data-testid="settings-wa-phone-id"
                />
              </div>
              <div className="settings-field">
                <Label>Business Account ID</Label>
                <Input
                  value={settings.whatsappBusinessAccountId || ""}
                  onChange={(e) => update("whatsappBusinessAccountId", e.target.value)}
                  placeholder="844007907961..."
                  data-testid="settings-wa-biz-id"
                />
              </div>
              <div className="settings-field">
                <Label>Webhook Verify Token</Label>
                <Input
                  value={settings.whatsappVerifyToken || ""}
                  onChange={(e) => update("whatsappVerifyToken", e.target.value)}
                  placeholder="your_secret_verify_token"
                  data-testid="settings-wa-verify-token"
                />
                <span className="settings-hint">Custom secret used to verify webhook from Meta</span>
              </div>
              <div className="settings-fathom-actions">
                <Button variant="outline" size="sm" onClick={testWhatsAppConnection} disabled={waTest.loading} data-testid="wa-test-btn">
                  {waTest.loading ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Testing...</> : "Test connection"}
                </Button>
                {waTest.result && (
                  <span className={`fathom-test-result ${waTest.result.success ? "fathom-test-ok" : "fathom-test-fail"}`} data-testid="wa-test-result">
                    {waTest.result.success ? <Check className="w-3.5 h-3.5" /> : <X className="w-3.5 h-3.5" />}
                    {waTest.result.message}
                  </span>
                )}
              </div>
              <div className="settings-fathom-instructions">
                <strong>How to set up WhatsApp Cloud API:</strong>
                <ol>
                  <li>Go to <a href="https://developers.facebook.com/apps" target="_blank" rel="noopener noreferrer">developers.facebook.com/apps</a></li>
                  <li>Select your app → WhatsApp → API Setup</li>
                  <li>Copy Access Token, Phone Number ID, and Business Account ID</li>
                  <li>Set Webhook URL to: <code>{backendUrl}/api/demo/whatsapp-webhook</code></li>
                  <li>Subscribe to <strong>messages</strong> webhook field</li>
                </ol>
              </div>
            </div>
          </div>

          {/* Automation Studio quick-link (replaces deprecated inline WhatsAppAutomation) */}
          <div className="settings-card settings-card-full" data-testid="settings-automation-link">
            <div className="settings-card-header">
              <Bot className="w-5 h-5" />
              <div>
                <h3>WhatsApp Automation</h3>
                <p>Edit AI Closer, sequences, knowledge base & quick replies in the dedicated Studio.</p>
              </div>
            </div>
            <div className="settings-card-body">
              <Button
                onClick={() => navigate("/demo/automation")}
                data-testid="settings-open-automation-btn"
              >
                <Bot className="w-4 h-4" /> Open Automation Studio
              </Button>
            </div>
          </div>

          {/* Closer Calendar — per-closer working hours synced to WhatsApp */}
          <CloserCalendar currentUser={auth} />

          {/* WhatsApp Booking Flow (Calendly-style native picker) */}
          <div className="settings-card settings-card-full" data-testid="settings-wa-flow">
            <div className="settings-card-header">
              <CalendarIcon className="w-5 h-5" />
              <div>
                <h3>WhatsApp Booking Calendar</h3>
                <p>Calendly-style native date + time picker inside WhatsApp. Replaces error-prone AI text parsing.</p>
              </div>
            </div>
            <div className="settings-card-body">
              <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 12 }}>
                <div style={{ fontSize: 13, color: "#475569" }}>
                  <strong>Status:</strong>{" "}
                  {flowStatus.configured ? (
                    <span style={{ color: "#16a34a", fontWeight: 600 }} data-testid="flow-status-active">
                      ✓ Active — Flow ID: <code style={{ fontSize: 12 }}>{flowStatus.flowId}</code>
                    </span>
                  ) : (
                    <span style={{ color: "#b91c1c", fontWeight: 600 }} data-testid="flow-status-inactive">
                      Not configured yet
                    </span>
                  )}
                </div>
                {flowStatus.endpointUrl && (
                  <div style={{ fontSize: 12, color: "#64748b" }}>
                    Endpoint: <code>{flowStatus.endpointUrl}</code>
                  </div>
                )}
              </div>

              <div className="settings-fathom-actions">
                <Button
                  onClick={() => runFlowSetup(false)}
                  disabled={flowSetup.loading}
                  data-testid="flow-setup-btn"
                >
                  {flowSetup.loading ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Setting up…</> : (flowStatus.configured ? "Re-publish flow" : "Setup booking flow")}
                </Button>
                {flowStatus.configured && (
                  <Button
                    variant="outline"
                    onClick={() => runFlowSetup(true)}
                    disabled={flowSetup.loading}
                    data-testid="flow-setup-force-btn"
                  >
                    Regenerate keys & re-publish
                  </Button>
                )}
              </div>

              {flowSetup.error && (
                <div style={{ marginTop: 10, padding: 10, background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 6, color: "#b91c1c", fontSize: 13 }} data-testid="flow-setup-error">
                  ❌ {flowSetup.error}
                </div>
              )}
              {flowSetup.success && (
                <div style={{ marginTop: 10, padding: 10, background: "#ecfdf5", border: "1px solid #bbf7d0", borderRadius: 6, color: "#15803d", fontSize: 13 }} data-testid="flow-setup-success">
                  ✓ Flow setup complete! Flow ID: <code>{flowSetup.success.flow_id}</code>
                </div>
              )}

              <div className="settings-fathom-instructions">
                <strong>What this does (one-time setup):</strong>
                <ol>
                  <li>Generates a 2048-bit RSA key pair on this server (private key stays here, never leaves).</li>
                  <li>Uploads the public key to your Meta WhatsApp Business Account so Meta can encrypt Flow data to us.</li>
                  <li>Creates a 3-screen Flow definition (date picker → time picker → confirm) in Meta&apos;s Flow Builder.</li>
                  <li>Publishes the Flow so your AI Closer can send it to leads automatically when they show booking intent.</li>
                </ol>
                <strong>Required:</strong> WhatsApp Cloud API token must have <code>whatsapp_business_management</code> scope, your business must be verified with Meta, and <code>BACKEND_BASE_URL</code> must point to your public domain.
              </div>
            </div>
          </div>

          {/* Fathom Integration */}
          <div className="settings-card" data-testid="settings-fathom">
            <div className="settings-card-header">
              <Mic className="w-5 h-5" />
              <div>
                <h3>Fathom Integration</h3>
                <p>Auto-sync call summaries, transcripts, and action items</p>
              </div>
            </div>
            <div className="settings-card-body">
              <div className="settings-field">
                <Label>Fathom API Key</Label>
                <Input
                  type="password"
                  value={settings.fathomApiKey || ""}
                  onChange={(e) => update("fathomApiKey", e.target.value)}
                  placeholder="fathom_live_..."
                  data-testid="settings-fathom-api-key"
                />
                <span className="settings-hint">Generate at fathom.video → Profile → Settings → API Access</span>
              </div>
              <div className="settings-field">
                <Label>Webhook Secret</Label>
                <Input
                  type="password"
                  value={settings.fathomWebhookSecret || ""}
                  onChange={(e) => update("fathomWebhookSecret", e.target.value)}
                  placeholder="whsec_..."
                  data-testid="settings-fathom-webhook-secret"
                />
                <span className="settings-hint">Shown when you create the webhook in Fathom Settings</span>
              </div>
              <div className="settings-fathom-actions">
                <Button variant="outline" size="sm" onClick={testFathomConnection} disabled={fathomTest.loading} data-testid="fathom-test-btn">
                  {fathomTest.loading ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Testing...</> : "Test connection"}
                </Button>
                {fathomTest.result && (
                  <span className={`fathom-test-result ${fathomTest.result.success ? "fathom-test-ok" : "fathom-test-fail"}`} data-testid="fathom-test-result">
                    {fathomTest.result.success ? <Check className="w-3.5 h-3.5" /> : <X className="w-3.5 h-3.5" />}
                    {fathomTest.result.message}
                  </span>
                )}
              </div>
              <div className="settings-fathom-instructions">
                <strong>How to set up Fathom integration:</strong>
                <ol>
                  <li>Go to fathom.video → sign in → Profile → Settings</li>
                  <li>Under "API Access" → "Generate API key" → paste above</li>
                  <li>Click "Add Webhook" and set Destination URL to:<br/><code>{backendUrl}/api/fathom-webhook</code></li>
                  <li>Check: My meetings, Meetings shared with me, Summary, Action items, Transcript</li>
                  <li>Copy the webhook secret → paste above</li>
                  <li>Click "Test connection" to verify</li>
                </ol>
                <p>Call data appears on lead cards automatically 2–3 min after each call.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}


/* Live preview of next free slots (respects current workHours config) */
function WorkHoursPreview({ api }) {
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [savedOnce, setSavedOnce] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${api}/api/demo/available-slots?days=3`);
      const d = await r.json();
      setSlots((d.slots || []).slice(0, 4));
      setSavedOnce(true);
    } catch (e) {
      setSlots([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  return (
    <div className="workhours-preview" data-testid="workhours-preview">
      <div className="workhours-preview-header">
        <span className="workhours-preview-label">Next 4 free slots (refresh after saving):</span>
        <button type="button" onClick={refresh} className="workhours-preview-refresh" disabled={loading} data-testid="workhours-refresh">
          {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <CalendarClock className="w-3 h-3" />}
          Refresh
        </button>
      </div>
      {slots.length === 0 && savedOnce && !loading && (
        <p className="workhours-preview-empty">No free slots in the next 3 days — consider expanding hours or adding days.</p>
      )}
      {slots.length > 0 && (
        <ul className="workhours-preview-list">
          {slots.map((s, i) => (
            <li key={i} data-testid={`workhours-slot-${i}`}>{s.both}</li>
          ))}
        </ul>
      )}
    </div>
  );
}
